// WidgetCard.js
// The dispatcher a view's grid renders for every card: it turns one widget
// definition plus the state of its function instance into the framed card the
// user sees. Everything a view needs to know about widgets is behind this one
// component, so the live view and the builder render cards identically.

import React from 'react';
import { Alert, Box, Button, Typography } from '@mui/material';
import WidgetFrame from './WidgetFrame';
import HeaderWidget from './HeaderWidget';
import TabsWidget from './TabsWidget';
import TableWidget from './TableWidget';
import StatWidget from './StatWidget';
import ChartWidget from './ChartWidget';
import FileWidget from './FileWidget';
import ShelfWidget from './ShelfWidget';
import {
  KIND_ALLOWED_DISPLAYS,
  KIND_DEFAULT_DISPLAY,
  entryLabel,
  findCompositeEntry,
} from '../../../utils/widgetModel';
import { applyChartSettings, chartSettingsFor } from '../../common/CompositeOutputTabs';
import { exportFileName } from '../../../utils/excelExport';
import { runExportStamp } from '../../../utils/runDisplay';

// The empty states are a sentence in the middle of the card rather than an
// Alert, because "nothing here yet" is not a problem the user must act on.
function CentredNote({ children }) {
  return (
    <Box
      sx={{
        height: '100%',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        textAlign: 'center',
        p: 1,
      }}
    >
      <Typography variant="body2" color="text.secondary">{children}</Typography>
    </Box>
  );
}

function WidgetCard({
  widgetId,
  widget,
  instanceState = null,
  companyName = null,
  onConfigure = null,
  draggable = false,
}) {
  const display = widget?.display || 'tabs';
  const config = widget?.config || {};
  const handleConfigure = onConfigure ? () => onConfigure(widgetId) : null;
  const boundEntry = instanceState ? findCompositeEntry(instanceState.outputs, widget?.binding) : null;

  // A header binds to nothing, so it never reaches the instance checks below and
  // never shows a provenance line.
  if (display === 'header') {
    return (
      <WidgetFrame
        title={widget?.title || ''}
        run={null}
        showProvenance={false}
        draggable={draggable}
        onConfigure={handleConfigure}
      >
        <HeaderWidget config={config} />
      </WidgetFrame>
    );
  }

  // The instance could not be resolved at all — the function behind this card
  // was deleted, and only removing or re-pointing the card fixes it.
  if (!instanceState) {
    return (
      <WidgetFrame
        title={widget?.title || 'Card'}
        run={null}
        draggable={draggable}
        onConfigure={handleConfigure}
      >
        <Alert severity="error">This card&apos;s function was deleted.</Alert>
      </WidgetFrame>
    );
  }

  const frameTitle = widget?.title || instanceState.functionName || 'Card';

  // The spinner answers "is this card empty because something is still on its
  // way?" — nothing else. Once a run has painted the card, the background
  // upgrade from the run's bounded preview to the complete stored result must
  // not spin over content that is already on screen and already readable.
  // A manual response can contain the complete result before its run metadata
  // arrives. It is displayable even when that separate read fails.
  const hasResult = Boolean(instanceState.run) || instanceState.fullLoaded === true;
  const awaitingFirstPaint = instanceState.loading && !hasResult;
  // A run the user just started (or queued behind Run All) over a card that has
  // something to show: grey it out under a spinner until the new result lands.
  // The background preview-to-complete upgrade is NOT a run and never busies.
  const busy = instanceState.running && !awaitingFirstPaint;

  if (instanceState.error) {
    return (
      <WidgetFrame
        title={frameTitle}
        run={instanceState.run}
        runPending={instanceState.runPending}
        runDocError={instanceState.runDocError}
        historySaveUnconfirmed={instanceState.historySaveUnconfirmed}
        hasUnpublishedChanges={instanceState.hasUnpublishedChanges}
        onRetryRunDoc={instanceState.onRetryRunDoc}
        versionNotice={instanceState.versionNotice}
        onDismissVersionNotice={instanceState.onDismissVersionNotice}
        loading={awaitingFirstPaint}
        busy={busy}
        draggable={draggable}
        onConfigure={handleConfigure}
      >
        <Alert severity="warning">{instanceState.error}</Alert>
      </WidgetFrame>
    );
  }

  if (instanceState.hydrationError) {
    return (
      <WidgetFrame
        title={frameTitle}
        run={instanceState.run}
        runPending={instanceState.runPending}
        runDocError={instanceState.runDocError}
        historySaveUnconfirmed={instanceState.historySaveUnconfirmed}
        hasUnpublishedChanges={instanceState.hasUnpublishedChanges}
        onRetryRunDoc={instanceState.onRetryRunDoc}
        versionNotice={instanceState.versionNotice}
        onDismissVersionNotice={instanceState.onDismissVersionNotice}
        loading={false}
        draggable={draggable}
        onConfigure={handleConfigure}
      >
        <Alert
          severity="warning"
          action={instanceState.onRetryResult ? (
            <Button
              color="inherit"
              size="small"
              onClick={instanceState.onRetryResult}
            >
              Retry
            </Button>
          ) : null}
        >
          Decode could not load the complete saved result. This card may only have a preview.
        </Alert>
      </WidgetFrame>
    );
  }

  if (!hasResult) {
    return (
      <WidgetFrame
        title={frameTitle}
        run={null}
        runPending={instanceState.runPending}
        runDocError={instanceState.runDocError}
        historySaveUnconfirmed={instanceState.historySaveUnconfirmed}
        onRetryRunDoc={instanceState.onRetryRunDoc}
        versionNotice={instanceState.versionNotice}
        onDismissVersionNotice={instanceState.onDismissVersionNotice}
        loading={awaitingFirstPaint}
        busy={busy}
        draggable={draggable}
        onConfigure={handleConfigure}
      >
        {/* While the view is still looking for this instance's latest run,
            "no run yet" would be a false statement, so the card waits. */}
        {instanceState.loading
          ? <CentredNote>Loading…</CentredNote>
          : <CentredNote>No completed run yet — run this function to populate this card.</CentredNote>}
      </WidgetFrame>
    );
  }

  if (display === 'tabs') {
    return (
      <WidgetFrame
        title={frameTitle}
        run={instanceState.run}
        runPending={instanceState.runPending}
        runDocError={instanceState.runDocError}
        historySaveUnconfirmed={instanceState.historySaveUnconfirmed}
        hasUnpublishedChanges={instanceState.hasUnpublishedChanges}
        onRetryRunDoc={instanceState.onRetryRunDoc}
        versionNotice={instanceState.versionNotice}
        onDismissVersionNotice={instanceState.onDismissVersionNotice}
        loading={awaitingFirstPaint}
        busy={busy}
        draggable={draggable}
        onConfigure={handleConfigure}
      >
        <TabsWidget instanceState={instanceState} companyName={companyName} />
      </WidgetFrame>
    );
  }

  const entry = boundEntry;

  // A conditional artifact, or a republished function that no longer produces
  // this entry: the card says so instead of rendering an empty shell.
  if (!entry) {
    return (
      <WidgetFrame
        title={frameTitle}
        run={instanceState.run}
        runPending={instanceState.runPending}
        runDocError={instanceState.runDocError}
        historySaveUnconfirmed={instanceState.historySaveUnconfirmed}
        hasUnpublishedChanges={instanceState.hasUnpublishedChanges}
        onRetryRunDoc={instanceState.onRetryRunDoc}
        versionNotice={instanceState.versionNotice}
        onDismissVersionNotice={instanceState.onDismissVersionNotice}
        loading={awaitingFirstPaint}
        busy={busy}
        draggable={draggable}
        onConfigure={handleConfigure}
      >
        <CentredNote>Not produced by this run.</CentredNote>
      </WidgetFrame>
    );
  }

  // A republish can change an entry's kind under a binding that still resolves
  // (a table becomes a chart, say). The card follows the entry rather than
  // trusting its saved display, which would otherwise render the wrong widget.
  const allowedDisplays = KIND_ALLOWED_DISPLAYS[entry.kind] || [];
  const effectiveDisplay = allowedDisplays.includes(display)
    ? display
    : KIND_DEFAULT_DISPLAY[entry.kind];

  // The function's live chart settings (from the View's head watch): the frame
  // and the chart widget overlay the same ones.
  const entrySettings = entry.kind === 'chart'
    ? chartSettingsFor(instanceState.chartSettings, entry.source_block_id, entry.key)
    : null;
  const framedEntry = entrySettings
    ? { ...entry, chart: applyChartSettings(entry.chart, entrySettings) }
    : entry;
  const title = widget?.title
    || (effectiveDisplay === 'stat' ? config.label : '')
    || entryLabel(framedEntry)
    || instanceState.functionName;

  let body = null;
  if (effectiveDisplay === 'table') {
    body = <TableWidget entry={entry} exportName={exportFileName(instanceState.functionName, runExportStamp(instanceState.run), entryLabel(entry))} />;
  } else if (effectiveDisplay === 'stat') {
    body = <StatWidget entry={entry} config={config} loading={instanceState.loading} />;
  } else if (effectiveDisplay === 'chart') {
    body = (
      <ChartWidget
        entry={entry}
        functionId={instanceState.functionId}
        companyName={companyName}
        savedSettings={entrySettings}
      />
    );
  } else if (effectiveDisplay === 'file') {
    body = <FileWidget entry={entry} />;
  } else if (effectiveDisplay === 'shelf') {
    body = <ShelfWidget entry={entry} />;
  } else {
    // The binding resolved to something no widget can draw (an entry kind added
    // after this build). Saying so beats crashing the whole view.
    body = <CentredNote>This result cannot be shown as a card.</CentredNote>;
  }

  return (
    <WidgetFrame
      title={title}
      run={instanceState.run}
      runPending={instanceState.runPending}
      runDocError={instanceState.runDocError}
      historySaveUnconfirmed={instanceState.historySaveUnconfirmed}
      hasUnpublishedChanges={instanceState.hasUnpublishedChanges}
      onRetryRunDoc={instanceState.onRetryRunDoc}
      versionNotice={instanceState.versionNotice}
      onDismissVersionNotice={instanceState.onDismissVersionNotice}
      loading={awaitingFirstPaint}
      busy={busy}
      draggable={draggable}
      onConfigure={handleConfigure}
    >
      {body}
    </WidgetFrame>
  );
}

export default WidgetCard;
